import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UsersThree, PaperPlaneTilt, Trash, Crown, Clock, Envelope, Seat } from "@phosphor-icons/react";

const inputCls = "w-full bg-surface-2 border border-border rounded-md px-4 py-3 text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent/40 transition-shadow";

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/team");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await api.post("/team/invite", { email });
      toast.success(`Invitation envoyée à ${email}`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setInviting(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/team/invite/${id}`);
      toast.success("Invitation annulée");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const resend = async (id, email) => {
    try {
      await api.post(`/team/invite/${id}/resend`);
      toast.success(`Invitation renvoyée à ${email}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeMember = async (id, name) => {
    if (!window.confirm(`Retirer ${name} de l'équipe ?`)) return;
    try {
      await api.delete(`/team/members/${id}`);
      toast.success("Membre retiré");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl">
        <div className="surface rounded-lg h-40 animate-pulse mb-4" />
        <div className="surface rounded-lg h-64 animate-pulse" />
      </div>
    );
  }

  if (!data) {
    return <div className="surface rounded-lg p-10 text-center text-muted" data-testid="team-error">Impossible de charger votre équipe.</div>;
  }

  const seats = data.seats || { used: 0, max: 0 };
  const full = seats.used >= seats.max;
  const isOwner = data.is_owner;

  return (
    <div className="max-w-3xl" data-testid="team-page">
      <div className="overline mb-2">Espace {data.plan ? data.plan : "équipe"}</div>
      <h1 className="font-display text-3xl font-bold mb-1 flex items-center gap-3"><UsersThree size={28} className="text-accent" weight="duotone" /> Mon équipe</h1>
      <p className="text-muted text-sm mb-8">Invitez vos collègues à rejoindre votre organisation interKO. Chaque membre consomme un siège de votre abonnement.</p>

      {/* Seats meter */}
      <div className="surface rounded-lg p-5 mb-6" data-testid="team-seats">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-fg font-display font-bold"><Seat size={18} className="text-accent" /> Sièges</div>
          <div className="text-sm font-mono text-muted"><span className="text-fg font-bold">{seats.used}</span> / {seats.max} utilisés</div>
        </div>
        <div className="h-2 rounded-full bg-surface-2 overflow-hidden">
          <div className="h-full bg-accent transition-all" style={{ width: `${Math.min(100, (seats.used / Math.max(1, seats.max)) * 100)}%` }} />
        </div>
        <div className="text-xs text-muted mt-2">{seats.members} membre{seats.members > 1 ? "s" : ""} · {seats.pending} invitation{seats.pending > 1 ? "s" : ""} en attente</div>
      </div>

      {/* Invite form */}
      <div className="surface rounded-lg p-5 mb-6">
        <div className="overline mb-3 flex items-center gap-2"><PaperPlaneTilt size={13} className="text-accent" weight="fill" /> Inviter un collègue</div>
        <form onSubmit={invite} className="flex flex-col sm:flex-row gap-3" data-testid="team-invite-form">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className={inputCls} placeholder="collegue@votre-esn.fr" data-testid="team-invite-email" disabled={full} />
          <button type="submit" disabled={inviting || full} data-testid="team-invite-submit"
            className="bg-accent text-black font-semibold px-6 py-3 rounded-md hover-lift flex items-center justify-center gap-2 shrink-0 disabled:opacity-60">
            <PaperPlaneTilt size={16} weight="fill" /> {inviting ? "Envoi…" : "Envoyer l'invitation"}
          </button>
        </form>
        {full && <p className="text-xs text-accent mt-3" data-testid="team-seats-full">Limite de sièges atteinte. Libérez un siège ou passez à une offre supérieure pour inviter plus de membres.</p>}
      </div>

      {/* Members */}
      <div className="surface rounded-lg p-5 mb-6">
        <div className="overline mb-4">Membres ({data.members.length})</div>
        <div className="space-y-2" data-testid="team-members">
          {data.members.map((m) => (
            <div key={m.id} className="flex items-center gap-3 bg-surface-2 border border-border rounded-md px-4 py-3" data-testid={`team-member-${m.id}`}>
              <div className="w-9 h-9 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center text-accent font-bold text-sm shrink-0">
                {(m.name || m.email).slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-fg font-medium truncate flex items-center gap-2">
                  {m.name || m.email}
                  {m.is_owner && <span className="inline-flex items-center gap-1 text-[10px] font-mono text-accent border border-accent/30 rounded-full px-2 py-0.5"><Crown size={10} weight="fill" /> Propriétaire</span>}
                  {m.is_self && <span className="text-[10px] font-mono text-muted">(vous)</span>}
                </div>
                <div className="text-xs text-muted truncate">{m.email}</div>
              </div>
              {isOwner && !m.is_owner && !m.is_self && (
                <button onClick={() => removeMember(m.id, m.name || m.email)} data-testid={`team-remove-${m.id}`}
                  className="p-2 rounded-md text-muted hover:text-red-400 hover:bg-red-500/10 hover-lift" title="Retirer">
                  <Trash size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Pending invites */}
      {data.invites.length > 0 && (
        <div className="surface rounded-lg p-5">
          <div className="overline mb-4 flex items-center gap-2"><Clock size={13} className="text-accent" /> Invitations en attente ({data.invites.length})</div>
          <div className="space-y-2" data-testid="team-invites">
            {data.invites.map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 bg-surface-2 border border-border rounded-md px-4 py-3" data-testid={`team-invite-${inv.id}`}>
                <Envelope size={18} className="text-muted shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-fg truncate">{inv.email}</div>
                  <div className="text-xs text-muted">Expire le {new Date(inv.expires_at).toLocaleDateString("fr-FR")}</div>
                </div>
                <button onClick={() => resend(inv.id, inv.email)} data-testid={`team-resend-${inv.id}`}
                  className="text-xs border border-border rounded-md px-3 py-1.5 text-muted hover:text-accent hover:border-accent/40 hover-lift flex items-center gap-1.5">
                  <PaperPlaneTilt size={12} /> Renvoyer
                </button>
                <button onClick={() => revoke(inv.id)} data-testid={`team-revoke-${inv.id}`}
                  className="text-xs border border-border rounded-md px-3 py-1.5 text-muted hover:text-red-400 hover:border-red-400/40 hover-lift">
                  Annuler
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
